import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SuperSearchResponse, SuperSearchResult, SuperSearchService } from 'api';

const SUPER_SEARCH_QUERY_KEY = 'super-search';
const SUPER_SEARCH_CACHE_SCHEMA = 'authorization-v2';
const SUPER_SEARCH_RESULT_LIMIT = 100;
const EPHEMERAL_SEARCH_GC_TIME = 5 * 60 * 1000;
const PERSISTED_SEARCH_GC_TIME = 24 * 60 * 60 * 1000;
const STABLE_SEARCH_STALE_TIME = 5 * 60 * 1000;

interface SuperSearchQueryOptions {
  authorizationRevision?: string | null;
  contextCompanyId?: number;
  contextFirmId?: number;
  contextMeasurementDate?: string;
  includeSavedDestinations?: boolean;
  includeDocuments?: boolean;
  requestKey?: string;
}

const isStableNavigationResult = (result: SuperSearchResult) =>
  result.type === SuperSearchResult.type.FIRM ||
  result.type === SuperSearchResult.type.COMPANY ||
  result.type === SuperSearchResult.type.FIRM_PAGE ||
  result.type === SuperSearchResult.type.COMPANY_PAGE ||
  result.type === SuperSearchResult.type.MEASUREMENT_DATE;

const useSuperSearchQuery = (
  query: string,
  enabled: boolean,
  {
    authorizationRevision,
    contextCompanyId,
    contextFirmId,
    contextMeasurementDate,
    includeSavedDestinations = false,
    includeDocuments = false,
    requestKey = query,
  }: SuperSearchQueryOptions = {}
) => {
  const queryClient = useQueryClient();
  const isLiveSearch = includeSavedDestinations || includeDocuments;
  // Persist eligibility follows authorization + family only. Closing the palette or
  // debouncing sets enabled=false; that must not flip meta.persist and strip IndexedDB.
  const canPersist = Boolean(authorizationRevision) && !isLiveSearch;
  const stableResultQueryKey = useMemo(
    () => [
      SUPER_SEARCH_QUERY_KEY,
      SUPER_SEARCH_CACHE_SCHEMA,
      authorizationRevision ?? 'session-only',
      'stable-results',
      requestKey,
      query,
      contextFirmId,
      contextCompanyId,
      contextMeasurementDate,
    ],
    [authorizationRevision, contextCompanyId, contextFirmId, contextMeasurementDate, query, requestKey]
  );
  const persistedStableData =
    authorizationRevision && isLiveSearch
      ? queryClient.getQueryData<SuperSearchResponse>(stableResultQueryKey)
      : undefined;

  const search = useQuery<SuperSearchResponse>({
    enabled,
    gcTime: canPersist ? PERSISTED_SEARCH_GC_TIME : EPHEMERAL_SEARCH_GC_TIME,
    meta: { persist: canPersist, showGlobalError: false },
    placeholderData: isLiveSearch
      ? previousData =>
        persistedStableData ??
          (previousData
            ? {
              results: previousData.results.filter(isStableNavigationResult),
            }
            : undefined)
      : previousData => previousData,
    queryFn: ({ signal }) => {
      const request = SuperSearchService.apiSuperSearchList(
        query || undefined,
        SUPER_SEARCH_RESULT_LIMIT,
        includeDocuments,
        includeSavedDestinations,
        contextFirmId,
        contextCompanyId,
        contextMeasurementDate
      );
      const cancelRequest = () => request.cancel();

      if (signal.aborted) {
        cancelRequest();
      } else {
        signal.addEventListener('abort', cancelRequest, { once: true });
      }

      return request.finally(() => signal.removeEventListener('abort', cancelRequest));
    },
    queryKey: [
      SUPER_SEARCH_QUERY_KEY,
      SUPER_SEARCH_CACHE_SCHEMA,
      authorizationRevision ?? 'session-only',
      requestKey,
      query,
      includeDocuments,
      includeSavedDestinations,
      contextFirmId,
      contextCompanyId,
      contextMeasurementDate,
    ],
    refetchOnMount: isLiveSearch ? 'always' : true,
    retry: false,
    staleTime: isLiveSearch ? 0 : STABLE_SEARCH_STALE_TIME,
  });

  // Persist only the authorization-safe navigation subset from mixed live responses.
  useEffect(() => {
    if (!authorizationRevision || !isLiveSearch || !search.data || search.isPlaceholderData) {
      return;
    }

    queryClient.setQueryData<SuperSearchResponse>(stableResultQueryKey, {
      results: search.data.results.filter(isStableNavigationResult),
    });
  }, [authorizationRevision, isLiveSearch, queryClient, search.data, search.isPlaceholderData, stableResultQueryKey]);

  return search;
};

export default useSuperSearchQuery;
